import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGroups } from '../../../../hooks/useGroups';
import { useContractsWithSubscribers } from '../../../../hooks/useContracts';
import { useSendPushNotification } from '../../../../hooks/usePushNotifications';
import {
  DEFAULT_FILTERS,
  DEFAULT_MESSAGE,
  OWNERSHIP_OPTIONS,
  EMPHASIS_WEIGHTS,
} from '../constants/pushNotificationConstants';

const ownershipFlag = (subscriber) =>
  Boolean(
    subscriber?.is_possessor ??
      subscriber?.isPoseedor ??
      subscriber?.poseedor ??
      subscriber?.is_owner ??
      subscriber?.isOwner ??
      false
  );

/**
 * Hook maestro del feature: maneja mensaje, filtros, audiencia y preview.
 * Encapsula los hooks de datos (groups/contacts) y expone props listas para UI.
 */
export const usePushNotificationComposer = () => {
  const { data: groupsData = [], isLoading: loadingGroups } = useGroups();
  const { data: contractsData = [], isLoading: loadingContracts } = useContractsWithSubscribers({
    requireDeviceToken: true,
  });
  const { mutateAsync: sendPushNotification, isPending: isSending } = useSendPushNotification();

  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [recipients, setRecipients] = useState([]);

  const groupOptions = useMemo(
    () =>
      groupsData.map((group) => ({
        id: group.uuid || group.id,
        label: group.name || group.group_name || group.nombre || 'Grupo sin nombre',
      })),
    [groupsData]
  );

  const contractOptions = useMemo(() => {
    const selectedGroupIds = new Set(filters.groups);

    return contractsData
      .filter((contract) => {
        if (selectedGroupIds.size > 0 && contract.uuid_group && !selectedGroupIds.has(contract.uuid_group)) {
          return false;
        }
        return true;
      })
      .map((contract) => ({
        ...contract,
        id: contract.uuid,
        label: `#${contract.contract_number}`,
        subtitle:
          contract.subscribers
            ?.map(
              (s) => `${s.first_name || s.nombre || ''} ${s.last_name || s.apellido || ''}`.trim()
            )
            .join(', ') || 'Sin suscriptores',
      }));
  }, [contractsData, filters.groups]);

  useEffect(() => {
    const selectedIds = new Set(filters.contracts);
    const groupIds = new Set(filters.groups);

    const eligibleContracts =
      filters.audienceScope === 'filtered'
        ? contractsData.filter((contract) => selectedIds.has(contract.uuid))
        : contractsData.filter((contract) => {
            if (groupIds.size > 0 && contract.uuid_group && !groupIds.has(contract.uuid_group)) {
              return false;
            }
            return true;
          });

    const collected = eligibleContracts
      .flatMap((contract) => contract.subscribers || [])
      .filter((subscriber) => {
        if (filters.ownership === 'all') return true;
        const isOwner = ownershipFlag(subscriber);
        return filters.ownership === 'owner' ? isOwner : !isOwner;
      })
      .map((subscriber) => ({
        uuid: subscriber.uuid,
        fullName: `${subscriber.first_name || subscriber.nombre || ''} ${subscriber.last_name || subscriber.apellido || ''}`.trim(),
        email: subscriber.email || subscriber.mail || subscriber.correo || '',
      }))
      .filter((subscriber) => subscriber.uuid);

    const deduped = Array.from(
      collected.reduce((map, subscriber) => map.set(subscriber.uuid, subscriber), new Map()).values()
    );

    setRecipients(deduped);
  }, [contractsData, filters.audienceScope, filters.contracts, filters.groups, filters.ownership]);

  const updateMessage = useCallback((field, value) => {
    setMessage((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const appendEmoji = useCallback((emoji) => {
    setMessage((prev) => ({
      ...prev,
      body: `${prev.body}${emoji}`,
    }));
  }, []);

  const updateFilters = useCallback((field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const resetDraft = useCallback(() => {
    setMessage(DEFAULT_MESSAGE);
    setFilters(DEFAULT_FILTERS);
    setRecipients([]);
  }, []);

  const audienceLabel = useMemo(() => {
    if (filters.audienceScope === 'all') {
      return 'Todos los suscriptores';
    }

    const ownership = OWNERSHIP_OPTIONS.find((option) => option.value === filters.ownership)?.label;
    const groupCount = filters.groups.length;
    const contractCount = filters.contracts.length;

    const segments = [];

    if (groupCount > 0) segments.push(`${groupCount} grupo(s)`);
    if (contractCount > 0) segments.push(`${contractCount} contrato(s)`);
    if (ownership && ownership !== 'Todos') segments.push(ownership);

    return segments.length > 0 ? segments.join(' | ') : 'Filtro pendiente';
  }, [filters.audienceScope, filters.contracts, filters.groups, filters.ownership]);

  const preview = useMemo(
    () => ({
      ...message,
      fontWeight: EMPHASIS_WEIGHTS[message.emphasis] || EMPHASIS_WEIGHTS.regular,
      audienceLabel,
      excerpt: message.body?.slice(0, 140) || '',
      generatedAt: new Date().toISOString(),
    }),
    [audienceLabel, message]
  );

  const payloadDraft = useMemo(
    () => ({
      title: message.title,
      body: message.body,
      emphasis: message.emphasis,
      audience: filters,
      recipients,
      // `generatedAt` sirve como referencia local para luego integrarlo al backend.
      generatedAt: preview.generatedAt,
    }),
    [filters, message, preview.generatedAt, recipients]
  );

  const sendToBackend = useCallback(async () => {
    const subscriberIds = recipients.map((recipient) => recipient.uuid).filter(Boolean);

    if (!subscriberIds.length) {
      throw new Error('No hay suscriptores seleccionados');
    }

    const payload = {
      title: message.title,
      body: message.body,
      subscribers: subscriberIds,
      emphasis: message.emphasis,
      generated_at: preview.generatedAt,
    };

    const response = await sendPushNotification(payload);
    return response;
  }, [message.body, message.emphasis, message.title, preview.generatedAt, recipients, sendPushNotification]);

  return {
    message,
    filters,
    preview,
    payloadDraft,
    recipients,
    groupOptions,
    contractOptions,
    loadingGroups,
    loadingContracts,
    isSending,
    updateMessage,
    appendEmoji,
    updateFilters,
    resetDraft,
    sendToBackend,
  };
};

export default usePushNotificationComposer;
